// src/components/portfolio/add-asset/index.js
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { db } from '../../../services/firebase/config';

import SearchModes from './components/SearchModes';
import SearchInput from './components/SearchInput';
import SearchResults from './components/SearchResults';
import SelectedSymbol from './components/SelectedSymbol';
import AllocationInput from './components/AllocationInput';
import { SearchService } from './services/SearchService';
import { TefasService } from './services/TefasService';
import { PositionService } from './services/PositionService';

const AddAssetModal = ({ 
  isOpen, 
  onClose, 
  onAdd, 
  parentCategory,
  portfolioData = {},
  selectedPortfolio
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedSymbol, setSelectedSymbol] = useState(null);
  const [allocation, setAllocation] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchMode, setSearchMode] = useState('local'); // Başlangıç modu 'local' olarak değiştirildi

  const loadPortfolioPositions = async () => {
    if (!selectedPortfolio?.id) return;

    setIsLoading(true);
    try {
      const results = await PositionService.searchPositions(selectedPortfolio.id, portfolioData);
      setSearchResults(results);
    } catch (error) {
      console.error('Error loading positions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Modal açıldığında
  useEffect(() => {
    if (isOpen && searchMode === 'portfolio') {
      loadPortfolioPositions();
    }
  }, [isOpen, searchMode, selectedPortfolio?.id]);

  const handleModalClose = () => {
    setSearchQuery('');
    setSearchResults([]);
    setSelectedSymbol(null);
    setAllocation('');
    setSearchMode('local'); // Modal kapandığında varsayılan moda dön
    if (onClose) onClose();
  };

  const handleSearch = async (query) => {
    if (searchMode === 'portfolio') {
      loadPortfolioPositions();
      return;
    }

    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsLoading(true);
    try {
      let results = [];
      
      switch (searchMode) {
        case 'fund':
          results = await TefasService.searchFunds(query);
          break;
        case 'currency':
          results = await SearchService.searchCurrencies(query);
          break;
        case 'local':
          results = await SearchService.searchLocal(query, db, 'local');
          break;
        case 'api':
          results = await SearchService.searchFMP(query);
          break;
        default:
          results = [];
      }

      setSearchResults(results);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSymbolSelect = (symbol) => {
    setSelectedSymbol(symbol);
    setSearchQuery(searchMode === 'currency' 
      ? symbol.name 
      : `${symbol.symbol} - ${symbol.name || ''}`
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSymbol || !allocation) return;

    try {
      let assetData = {
        symbol: selectedSymbol.symbol,
        description: selectedSymbol.name || '',
        allocation: parseFloat(allocation),
        type: selectedSymbol.type || 'STOCK',
        quantity: 0,
        lastPrice: selectedSymbol.info?.lastPrice
      };

      if (searchMode === 'currency') {
        assetData = {
          ...assetData,
          symbol: `${selectedSymbol.symbol}.CURRENCY`,
          type: 'CURRENCY',
          currencySymbol: selectedSymbol.currencySymbol
        };
      }

      onAdd(assetData);
      handleModalClose();
    } catch (error) {
      console.error('Error adding symbol:', error);
      alert('Varlık eklenirken bir hata oluştu');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={handleModalClose} />
      
      <div className="relative z-50 w-full max-w-md m-4 bg-white dark:bg-[#151934] rounded-lg shadow-xl">
        <div className="p-6">
          <button 
            onClick={handleModalClose}
            className="absolute top-4 right-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"
          >
            <X className="w-5 h-5" />
          </button>

          <h3 className="text-lg font-medium mb-4 text-gray-900 dark:text-white">
            {parentCategory ? `${parentCategory} için Varlık Ekle` : 'Yeni Varlık Ekle'}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <SearchModes 
              searchMode={searchMode} 
              onModeChange={(mode) => {
                setSearchMode(mode);
                setSelectedSymbol(null);
                setSearchQuery('');
                if (mode === 'portfolio') {
                  loadPortfolioPositions();
                } else {
                  setSearchResults([]);
                }
              }}
            />

            {searchMode !== 'portfolio' && (
              <SearchInput
                value={searchQuery}
                onChange={handleSearch}
                isLoading={isLoading}
                mode={searchMode}
              />
            )}

            <div className="max-h-96 overflow-y-auto">
              {isLoading ? (
                <div className="text-center py-4 text-gray-500">
                  Yükleniyor...
                </div>
              ) : (
                <SearchResults 
                  results={searchResults}
                  searchMode={searchMode}
                  onSelect={handleSymbolSelect}
                />
              )}
            </div>

            {selectedSymbol && (
              <>
                <SelectedSymbol
                  symbol={selectedSymbol}
                  searchMode={searchMode}
                  onClear={() => {
                    setSelectedSymbol(null);
                    setSearchQuery('');
                    if (searchMode === 'portfolio') {
                      loadPortfolioPositions();
                    }
                  }}
                />

                <AllocationInput
                  value={allocation}
                  onChange={setAllocation}
                />
              </>
            )}

            <div className="flex justify-end space-x-2 pt-4">
              <button
                type="button"
                onClick={handleModalClose}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300"
              >
                İptal
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 
                  dark:bg-blue-500 dark:hover:bg-blue-600 disabled:opacity-50"
                disabled={!selectedSymbol || !allocation}
              >
                Ekle
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddAssetModal;